/**
 * T-09 · Entrar, en dos pasos — F2-Spec.
 *
 *   1. Pedir el código → **nuestra API**, que ve el teléfono y nunca el código.
 *   2. Verificar       → **Supabase directo**, que ve el código y nunca lo manda aquí.
 *
 * `Q-18` se sostiene por esa separación: NestJS no puede leer el código ni
 * queriendo, porque nunca pasa por él. Y el token que devuelve Supabase se
 * cambia por nuestra cookie en `POST /autenticacion/sesion`.
 */
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { normalizarONulo } from '@fialrest/shared'
import type { TelefonoE164 } from '@fialrest/shared'
import { esquemaPedirCodigo } from '@fialrest/shared'
import { Boton, Campo, Aviso, Tarjeta } from '@fialrest/ui'
import { pedir, ErrorDeApi } from '@fialrest/cliente'
import { CLAVE_YO } from '../api/sesion.js'
import { verificarCodigo } from '@fialrest/cliente'

/**
 * `ADR-030` · Recordar el teléfono **en ese navegador**.
 *
 * El campo viene escrito y sólo hay que tocar «Mandar código». Es la tercera
 * pata de lo que hace rápido el ingreso, detrás de la cookie y del
 * autocompletado del SMS.
 *
 * No revela nada que quien está delante no sepa: **es su propio número en su
 * propio equipo**, y es lo mismo que guarda el autorrellenado del navegador. No
 * es una sesión ni vale para entrar: sin la cookie o sin un código nuevo, aquí
 * no se pasa.
 *
 * `try/catch` porque `localStorage` **lanza** en modo privado de Safari y con
 * las cookies de terceros apagadas en algunos navegadores. Que no se pueda
 * recordar un teléfono no puede tumbar la pantalla de ingreso.
 */
const LLAVE_TELEFONO = 'fialrest.entrar.telefono'

const telefonoRecordado = (): string => {
  try { return localStorage.getItem(LLAVE_TELEFONO) ?? '' } catch { return '' }
}

const recordarTelefono = (numero: string): void => {
  try { localStorage.setItem(LLAVE_TELEFONO, numero) } catch { /* sin recuerdo, igual se entra */ }
}

/** El código: seis dígitos, que es lo que manda Twilio Verify. */
const esquemaCodigo = z.object({
  codigo: z.string().trim().regex(/^\d{6}$/, 'Son seis dígitos.'),
})

const esquemaCafeterias = z.object({
  elige: z.array(z.object({ tenantId: z.string(), staffUserId: z.string() })),
})
const esquemaEntrada = z.union([
  z.object({ entro: z.literal(true), tenantId: z.string() }),
  esquemaCafeterias,
])

export function Entrar(): ReactNode {
  //  ── El teléfono NORMALIZADO, y no el que se tecleó ──────────────────────
  //  Aquí estaba el fallo, y llevaba desde `F2`: se guardaba el crudo —«662 467
  //  4951»— y con él se verificaba el código contra Supabase, que había creado
  //  el par (teléfono, código) para `+526624674951`, que es lo que nuestra API
  //  le mandó. No encontraba nada y contestaba «caducado o inválido» con un
  //  código recién llegado y correcto.
  //
  //  Nadie lo vio porque **el panel no estaba en la lista de orígenes de la
  //  API**: no se podía levantar en local, así que su ingreso no se había
  //  recorrido nunca. El mostrador y el menú lo hacían bien desde el principio.
  const [telefono, setTelefono] = useState<TelefonoE164 | null>(null)
  const [fallo, setFallo] = useState<string | null>(null)
  const navegar = useNavigate()
  const cliente = useQueryClient()

  const formTelefono = useForm({
    resolver: zodResolver(esquemaPedirCodigo),
    defaultValues: { telefono: telefonoRecordado() },
  })
  const formCodigo = useForm({ resolver: zodResolver(esquemaCodigo) })

  const pedirCodigo = formTelefono.handleSubmit(async ({ telefono: t }) => {
    setFallo(null)
    //  Se normaliza ANTES de pedir, y se guarda lo normalizado: es lo mismo que
    //  la API va a mandarle a Supabase, y lo único con lo que el código se puede
    //  verificar después.
    const normalizado = normalizarONulo(t)
    if (normalizado === null) {
      setFallo('Ese número no parece válido. Con lada, así: 662 123 4567.')
      return
    }
    try {
      await pedir('/autenticacion/codigo', z.object({ encolado: z.literal(true) }),
                  { metodo: 'POST', cuerpo: { telefono: normalizado } })
      //  Se recuerda cuando el número **sirvió**, no al teclearlo: guardar lo que
      //  la API acaba de rechazar deja el campo con algo que ya se sabe que falla.
      recordarTelefono(normalizado)
      setTelefono(normalizado)
    } catch (e) {
      // El 429 trae cuánto falta. Decir «espera» sin decir cuánto es lo que hace
      // que la gente reintente cada diez segundos.
      setFallo(e instanceof Error ? e.message : 'No se pudo mandar el código.')
    }
  })

  const entrar = formCodigo.handleSubmit(async ({ codigo }) => {
    setFallo(null)
    try {
      // El código va a Supabase, NO a nuestra API.
      const token = await verificarCodigo(telefono!, codigo)
      const salida = await pedir('/autenticacion/sesion', esquemaEntrada,
                                 { metodo: 'POST', cuerpo: { token } })
      if ('elige' in salida) {
        // Varias cafeterías. Se resuelve en su pantalla; por ahora se entra a la
        // primera para no dejar el flujo cortado, y queda anotado para T-09b.
        await pedir('/autenticacion/sesion', esquemaEntrada,
                    { metodo: 'POST', cuerpo: { token, tenantId: salida.elige[0]!.tenantId } })
      }
      await cliente.invalidateQueries({ queryKey: CLAVE_YO })
      void navegar('/menu', { replace: true })
    } catch (e) {
      setFallo(e instanceof ErrorDeApi ? e.message : 'Ese código no funcionó.')
    }
  })

  return (
    <main style={{
      fontFamily: 'var(--fuente-ui)', color: 'var(--neutro-900)',
      background: 'var(--neutro-000)', minHeight: '100vh',
      display: 'grid', placeItems: 'center', padding: 'var(--e5)',
    }}>
      <Tarjeta>
        <div style={{ display: 'grid', gap: 'var(--e4)', width: 'min(360px, 84vw)' }}>
          <h1 style={{
            fontFamily: 'var(--fuente-ui)', fontWeight: 700, letterSpacing: '-0.02em',
            fontSize: 27, lineHeight: 1.1, margin: 0,
          }}>
            Entrar
          </h1>

          {fallo ? <Aviso>{fallo}</Aviso> : null}

          {telefono === null ? (
            <form onSubmit={(e) => { e.preventDefault(); void pedirCodigo() }}
                  style={{ display: 'grid', gap: 'var(--e3)' }}>
              <Campo
                rotulo="Tu teléfono"
                inputMode="tel"
                autoComplete="tel"
                placeholder="662 467 4951"
                error={formTelefono.formState.errors.telefono?.message}
                {...formTelefono.register('telefono')}
              />
              <Boton type="submit" variante="principal" ocupado={formTelefono.formState.isSubmitting}>
                {formTelefono.formState.isSubmitting ? 'Mandando…' : 'Mandar código'}
              </Boton>
            </form>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); void entrar() }}
                  style={{ display: 'grid', gap: 'var(--e3)' }}>
              <p style={{ color: 'var(--neutro-600)', fontSize: 13, margin: 0 }}>
                Te mandamos un código a <strong>{telefono}</strong>.
              </p>
              <Campo
                rotulo="Código"
                inputMode="numeric"
                // `one-time-code` es lo que hace que el teléfono ofrezca pegarlo
                // desde el SMS. Sin él hay que salir de la app a copiarlo.
                autoComplete="one-time-code"
                maxLength={6}
                error={formCodigo.formState.errors.codigo?.message}
                {...formCodigo.register('codigo')}
              />
              <Boton type="submit" variante="principal" ocupado={formCodigo.formState.isSubmitting}>
                {formCodigo.formState.isSubmitting ? 'Entrando…' : 'Entrar'}
              </Boton>
              <Boton onClick={() => { setTelefono(null); setFallo(null) }}>
                Usar otro teléfono
              </Boton>
            </form>
          )}
        </div>
      </Tarjeta>
    </main>
  )
}
